import { useMemo, useState, useEffect } from 'react'
import { useSnapshot } from 'valtio'
import { lastConnectOptions } from '../appStatus'
import { filterPackets } from './packetsFilter'
import { packetsReplayState } from './state/packetsReplayState'
import PacketList from './components/replay/PacketList'
import FilterInput from './components/replay/FilterInput'
import styles from './components/replay/ReplayPanel.module.css'
import type { ReplayEvent } from '../packetsReplay/extractReplayEvents'

interface Props {
  replayName: string
  packets: readonly PacketData[]
  isPlaying: boolean
  progress: { current: number; total: number }
  speed: number
  defaultFilter?: string
  customButtons: Readonly<Record<string, { state: boolean; label: string; tooltip?: string }>>
  onPlayPause?: (isPlaying: boolean) => void
  onRestart?: () => void
  onSpeedChange?: (speed: number) => void
  onFilterChange: (filter: string) => void
  onCustomButtonToggle: (buttonId: string) => void
  clientPacketsAutocomplete: string[]
  serverPacketsAutocomplete: string[]
  style?: React.CSSProperties
  replayEvents?: ReplayEvent[]
  totalTimeMs?: number
  onSeekToTime?: (timeMs: number) => void
  hidePlaybackBar?: boolean
}

export default function ReplayPanel ({
  replayName,
  packets,
  isPlaying,
  progress,
  speed,
  replayEvents,
  totalTimeMs,
  onSeekToTime,
  defaultFilter = '',
  customButtons,
  onPlayPause,
  onRestart,
  onSpeedChange,
  onFilterChange,
  onCustomButtonToggle,
  clientPacketsAutocomplete,
  serverPacketsAutocomplete,
  style,
  hidePlaybackBar = false,
}: Props) {
  const [filter, setFilter] = useState(defaultFilter)
  const { isMinimized, isRecording, showPacketList, seekTo } = useSnapshot(packetsReplayState)
  const isReplaySession = !!lastConnectOptions.value?.worldStateFileContents
  const recordingUiMode = isRecording && !isReplaySession
  const { filtered: filteredPackets, hiddenCount } = useMemo(() => {
    if (!showPacketList) return { filtered: [] as PacketData[], hiddenCount: 0 }
    return filterPackets(packets.slice(-500), filter)
  }, [showPacketList, packets, filter])

  const requestSeek = (nextCurrent: number) => {
    if (recordingUiMode) return
    const clamped = Math.max(0, Math.min(progress.total, nextCurrent))
    packetsReplayState.seekTo = clamped
    // If user seeks backward, use internal rewind (no reconnect needed).
    const seekBase = packetsReplayState.progress.current
    if (clamped < seekBase) {
      packetsReplayState.internalRewindTo = clamped
      packetsReplayState.seekTo = null
    }
  }

  useEffect(() => {
    onFilterChange(filter)
  }, [filter, onFilterChange])

  const handlePlayPauseClick = () => {
    if (isMinimized) {
      packetsReplayState.isMinimized = false
    } else {
      onPlayPause?.(!isPlaying)
    }
  }

  const playPauseBtnClass = `${styles.pbBtn} ${isPlaying ? styles.pause : styles.active}`

  const playPauseButton = recordingUiMode ? (
    <button
      onClick={handlePlayPauseClick}
      className={`${styles.pbBtn} ${styles.pause}`}
    >
      Rec
    </button>
  ) : (
    <button
      onClick={handlePlayPauseClick}
      className={playPauseBtnClass}
    >
      {isPlaying ? '⏸ Pause' : '▶ Play'}
    </button>
  )

  const baseContainerStyle = {
    position: 'fixed' as const,
    top: 18,
    right: 0,
    zIndex: 9,
  }

  if (hidePlaybackBar && isMinimized) return null

  if (isMinimized) {
    return (
      <div
        style={{
          ...baseContainerStyle,
          width: 'auto',
          padding: '16px',
          ...style,
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
          document.exitPointerLock?.()
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
          document.exitPointerLock?.()
        }}
        data-replay-ui
      >
        {playPauseButton}
      </div>
    )
  }

  return (
    <div
      style={{
        ...baseContainerStyle,
        maxHeight: '80vh',
        right: '20px',
        top: '60px',
        ...style,
      }}
      className={styles.packetPanel}
      onMouseDown={(e) => {
        e.stopPropagation()
        document.exitPointerLock?.()
      }}
      onPointerDown={(e) => {
        e.stopPropagation()
        document.exitPointerLock?.()
      }}
      data-replay-ui
    >
      <div
        className={styles.packetHeader}
      >
        <div className={styles.packetTitleWrap}>
          <div className={styles.packetFilename}>{replayName || 'Unnamed Replay'}</div>
          <div className={styles.packetSubtitle}>
            {recordingUiMode ? 'Recording packets...' : 'Integrated server emulation · Testing client...'}
          </div>
        </div>
        <div className={styles.packetHeaderBtns}>
          <button className={styles.pillBtn} onClick={() => { packetsReplayState.showPacketList = !showPacketList }}>
            {showPacketList ? 'Hide packets' : 'Show packets'}
          </button>
          <button className={styles.closeBtn} onClick={() => {
            if (hidePlaybackBar) {
              packetsReplayState.showPacketList = false
            } else {
              packetsReplayState.isMinimized = true
            }
          }}>
            ✕
          </button>
        </div>
      </div>

      {showPacketList && (
        <>
          <FilterInput
            value={filter}
            onChange={setFilter}
            hiddenCount={hiddenCount}
            shownCount={filteredPackets.length}
            onClearFilter={() => setFilter('')}
            clientPacketsAutocomplete={clientPacketsAutocomplete}
            serverPacketsAutocomplete={serverPacketsAutocomplete}
          />

          <PacketList
            packets={filteredPackets}
            maxHeight={340}
          />
        </>
      )}

      {!hidePlaybackBar && (
        <div className={styles.playbackBar}>
          <div className={styles.pbControls}>
            {playPauseButton}
            <button className={`${styles.pbBtn} ${styles.seekBtn}`} onClick={() => onSpeedChange?.(1)} title="Speed 1x">›</button>
            <button className={`${styles.pbBtn} ${styles.seekBtn}`} onClick={() => onSpeedChange?.(2)} title="Speed 2x">»»</button>
            <button className={`${styles.pbBtn} ${styles.restart}`} onClick={onRestart}>↺ Restart</button>

            <div className={styles.pbMeta}>
              <div className={styles.pbSpeed}>{recordingUiMode ? 'Rec' : (isPlaying ? 'Play' : 'Pause')} ×{speed}</div>
              <div className={styles.pbCounter}>
                <strong>{typeof seekTo === 'number' ? seekTo : progress.current}</strong> / {progress.total}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export interface PacketData {
  name: string
  data: any
  isFromClient: boolean
  isUpcoming: boolean
  actualVersion?: any
  position: number
  timestamp: number
  isCustomChannel?: boolean
}
